import React, { useMemo, useState } from 'react';
import { Send } from 'lucide-react';
import { emailConfig } from '../config/emailConfig';
import { useLocale } from '../context/LocaleContext';
import ContactPhoneInput from './ContactPhoneInput';

const ContactForm = ({ className = '' }) => {
  const { t } = useLocale();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSending, setIsSending] = useState(false);

  const subjectOptions = useMemo(
    () => [
      { value: 'projet', label: t('contact_subject_project') },
      { value: 'emploi', label: t('contact_subject_job') },
      { value: 'collaboration', label: t('contact_subject_collab') },
      { value: 'autre', label: t('contact_subject_other') },
    ],
    [t],
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (phone) => {
    setFormData((prev) => ({ ...prev, phone: phone || '' }));

    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('contact_error_name');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contact_error_email');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact_error_email_invalid');
    }

    if (!formData.subject.trim()) {
      newErrors.subject = t('contact_error_subject');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contact_error_message');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');

    if (!validateForm()) return;

    setIsSending(true);

    try {
      const response = await fetch(emailConfig.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || '',
          subject: formData.subject,
          message: formData.message,
        }),
      });

      const responseClone = response.clone();
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');

      if (!response.ok) {
        let errorMessage = t('contact_error_send');
        try {
          if (isJson) {
            const errorData = await responseClone.json();
            errorMessage = errorData.error || errorMessage;
          } else {
            const errorText = await responseClone.text();
            errorMessage = errorText || errorMessage;
          }
        } catch {
          errorMessage = `Erreur ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = isJson ? await response.json() : { success: false, error: t('contact_error_send') };

      if (!data.success) {
        throw new Error(data.error || t('contact_error_send'));
      }

      setIsSubmitted(true);
      resetForm();
    } catch (error) {
      const isNetworkError =
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError') ||
        error.message.includes('Network request failed');

      if (isNetworkError) {
        const subject = encodeURIComponent(`Contact Portfolio - ${formData.subject}`);
        const body = encodeURIComponent(
          `Nom: ${formData.name}\n` +
            `Email: ${formData.email}\n` +
            `Téléphone: ${formData.phone || '—'}\n` +
            `Sujet: ${formData.subject}\n\n` +
            `Message:\n${formData.message}`,
        );

        window.location.href = `mailto:${emailConfig.toEmail}?subject=${subject}&body=${body}`;
        setIsSubmitted(true);
        resetForm();
        return;
      }

      setSubmitError(error.message || t('contact_error_send'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className={`contact-form ${className}`.trim()}>
      <div className="contact-form__panel rihen-card rihen-card--flat">
        <header className="contact-form__head">
          <p className="contact-form__eyebrow">{t('contact_form_label')}</p>
          <h3 className="contact-form__title">{t('contact_form_title')}</h3>
          <p className="contact-form__subtitle">{t('contact_form_subtitle')}</p>
        </header>

        {submitError ? (
          <div className="contact-form__alert contact-form__alert--error" role="alert">
            <p>{submitError}</p>
          </div>
        ) : null}

        {isSubmitted ? (
          <div className="contact-form__success">
            <h4 className="contact-form__success-title">{t('contact_success_title')}</h4>
            <p className="contact-form__success-text">{t('contact_success_text')}</p>
            <button
              type="button"
              onClick={() => {
                setIsSubmitted(false);
                setSubmitError('');
              }}
              className="rihen-btn rihen-btn-outline interactive-button contact-form__success-btn"
            >
              <Send size={14} aria-hidden="true" />
              {t('contact_success_again')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="contact-form__fields" noValidate>
            <div className="contact-form__row">
              <div className="contact-form__field">
                <label htmlFor="contact-name" className="contact-form__label">
                  <span className="contact-form__label-text">
                    {t('contact_name_label')}
                    <span className="contact-form__required" aria-hidden="true"> *</span>
                  </span>
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`rihen-input ${errors.name ? 'is-error' : ''}`}
                  placeholder={t('contact_name_placeholder')}
                  autoComplete="name"
                />
                {errors.name ? <p className="contact-form__error">{errors.name}</p> : null}
              </div>

              <div className="contact-form__field">
                <label htmlFor="contact-email" className="contact-form__label">
                  <span className="contact-form__label-text">
                    {t('contact_email_label')}
                    <span className="contact-form__required" aria-hidden="true"> *</span>
                  </span>
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`rihen-input ${errors.email ? 'is-error' : ''}`}
                  placeholder={t('contact_email_placeholder')}
                  autoComplete="email"
                />
                {errors.email ? <p className="contact-form__error">{errors.email}</p> : null}
              </div>
            </div>

            <div className="contact-form__field">
              <label htmlFor="contact-phone" className="contact-form__label">
                <span className="contact-form__label-text">
                  {t('contact_phone_label')}
                  <span className="contact-form__optional"> ({t('contact_phone_optional')})</span>
                </span>
              </label>
              <ContactPhoneInput
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder={t('contact_phone_placeholder')}
              />
            </div>

            <div className="contact-form__field">
              <label htmlFor="contact-subject" className="contact-form__label">
                <span className="contact-form__label-text">
                  {t('contact_subject_label')}
                  <span className="contact-form__required" aria-hidden="true"> *</span>
                </span>
              </label>
              <select
                id="contact-subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className={`rihen-input ${errors.subject ? 'is-error' : ''}`}
              >
                <option value="">{t('contact_subject_placeholder')}</option>
                {subjectOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.subject ? <p className="contact-form__error">{errors.subject}</p> : null}
            </div>

            <div className="contact-form__field">
              <label htmlFor="contact-message" className="contact-form__label">
                <span className="contact-form__label-text">
                  {t('contact_message_label')}
                  <span className="contact-form__required" aria-hidden="true"> *</span>
                </span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                className={`rihen-input contact-form__textarea ${errors.message ? 'is-error' : ''}`}
                placeholder={t('contact_message_placeholder')}
              />
              {errors.message ? <p className="contact-form__error">{errors.message}</p> : null}
            </div>

            <button
              type="submit"
              disabled={isSending}
              className="rihen-btn rihen-btn-primary interactive-button contact-form__submit"
            >
              {isSending ? (
                <>
                  <span className="contact-form__spinner" aria-hidden="true" />
                  {t('contact_submitting')}
                </>
              ) : (
                <>
                  <Send size={16} aria-hidden="true" />
                  {t('contact_submit')}
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactForm;
